/**
 * Contracts of the admin categories, items and system prompt API — mirrors the backend's
 * `models/categories.py`, `models/items.py` and `models/prompts.py` (see `docs/api/openapi.json`).
 */

import type { Category } from '@/types/documents.types';

export interface CategoryModel {
  key: Category;
  label_cs: string;
  color: string;
  tint: string;
  /** System: the design's text; custom: derived from its items ("regex + LLM"), "—" when empty. */
  methods: string;
  is_system: boolean;
  sort_order: number;
  created_by: string;
  updated_by: string | null;
  updated_at: string;
  /** What the caller may do with it. */
  renamable: boolean;
  deletable: boolean;
}

export interface PaletteColorModel {
  color: string;
  tint: string;
  /** Not used by any category yet. */
  free: boolean;
}

/** `label_cs` 3–80 characters, unique case-insensitively; `color` a free palette colour. */
export interface CreateCategory {
  label_cs: string;
  color: string;
}

export interface UpdateCategoryDefinition {
  label_cs: string;
}

export type ItemType = 'LLM' | 'regex' | 'ENUM';

export const ITEM_TYPES: readonly ItemType[] = ['LLM', 'regex', 'ENUM'];

export interface ItemModel {
  /** Identifier slug `^[a-z0-9_]+$`, immutable. */
  id: string;
  /** "Popis". */
  name: string;
  type: ItemType;
  /** LLM prompt or regex; empty after a type switch. */
  config: string;
  /** ENUM values. */
  values: string[];
  /** Category keys in category sort order. */
  categories: Category[];
  /** Creator's username; `default` for seeded rows. */
  author: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  /** The caller may change name/type/config and delete it (owner or superadmin). */
  editable: boolean;
}

export interface CreateItem {
  id: string;
  name: string;
  type: ItemType;
  config?: string;
  values?: string[];
  categories?: Category[];
}

export interface UpdateItem {
  name?: string;
  type?: ItemType;
  config?: string;
  values?: string[];
}

export interface RegexMatch {
  start: number;
  end: number;
  text: string;
}

export interface RegexTestResult {
  /** The Python `re` error, or `null` when the pattern compiles. */
  error: string | null;
  matches: RegexMatch[];
}

/** The two LLM prompts behind `/prompts/{key}`: item detection and the document's own sensitivity declarations. */
export type PromptKind = 'system' | 'declaration';

export interface SystemPromptModel {
  /** Raw template, placeholders included. */
  text: string;
  /** `default` until a superadmin saves. */
  author: string;
  updated_at: string;
  is_default: boolean;
  default_text: string;
}
